/**
 * Knowledge Base Search - Client-side with Fuse.js
 *
 * Provides instant search for popular articles using Fuse.js fuzzy search.
 * Falls back to server-side search for comprehensive results.
 */

import Fuse from 'fuse.js';

window.kbFuseSearch = null;

// Initialize Fuse.js search when DOM is ready
document.addEventListener('DOMContentLoaded', function() {
    // Fuse.js search function
    window.kbFuseSearch = function(query, articles) {
        if (!query || !articles || articles.length === 0) {
            return [];
        }

        // Configure Fuse.js options
        const options = {
            keys: [
                {
                    name: 'title',
                    weight: 0.7
                },
                {
                    name: 'excerpt',
                    weight: 0.3
                }
            ],
            threshold: 0.3, // Lower = more strict matching
            distance: 100,
            minMatchCharLength: 2,
            includeScore: true,
            includeMatches: true
        };

        // Create Fuse instance
        const fuse = new Fuse(articles, options);

        // Perform search
        const results = fuse.search(query);

        // Format results
        return results.slice(0, 5).map(result => ({
            id: result.item.id,
            title: result.item.title,
            slug: result.item.slug,
            excerpt: highlightMatches(result.item.excerpt, result.matches),
            score: result.score
        }));
    };

    /**
     * Highlight search matches in text
     */
    function highlightMatches(text, matches) {
        if (!matches || matches.length === 0) {
            return text;
        }

        // Get matches for excerpt field
        const excerptMatches = matches.filter(m => m.key === 'excerpt');

        if (excerptMatches.length === 0) {
            return text;
        }

        // Sort matches by index
        const allIndices = [];
        excerptMatches.forEach(match => {
            match.indices.forEach(([start, end]) => {
                allIndices.push({ start, end });
            });
        });

        allIndices.sort((a, b) => a.start - b.start);

        // Build highlighted text
        let highlighted = '';
        let lastIndex = 0;

        allIndices.forEach(({ start, end }) => {
            highlighted += escapeHtml(text.substring(lastIndex, start));
            highlighted += '<mark class="bg-yellow-300 font-semibold">';
            highlighted += escapeHtml(text.substring(start, end + 1));
            highlighted += '</mark>';
            lastIndex = end + 1;
        });

        highlighted += escapeHtml(text.substring(lastIndex));

        return highlighted;
    }

    /**
     * Escape HTML to prevent XSS
     */
    function escapeHtml(text) {
        const div = document.createElement('div');
        div.textContent = text;
        return div.innerHTML;
    }
});

// Debounce helper function
function debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
        const later = () => {
            clearTimeout(timeout);
            func(...args);
        };
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
    };
}

// Export for module systems
if (typeof module !== 'undefined' && module.exports) {
    module.exports = { kbFuseSearch };
}
